// Cross-platform network status with native accuracy on mobile.
// On native uses @capacitor/network for accurate cellular/wifi state and
// transition events; on web falls back to navigator.onLine + window events.

import { Capacitor } from "@capacitor/core";

export interface NetworkState {
  connected: boolean;
  connectionType: "wifi" | "cellular" | "ethernet" | "unknown" | "none";
}

type Listener = (state: NetworkState) => void;

let lastState: NetworkState = {
  connected: typeof navigator !== "undefined" ? navigator.onLine : true,
  connectionType: "unknown",
};
const listeners = new Set<Listener>();
let started = false;

function emit(state: NetworkState) {
  lastState = state;
  for (const l of listeners) {
    try { l(state); } catch (err) { console.warn("[native-network] listener", err); }
  }
}

async function start() {
  if (started) return;
  started = true;
  if (Capacitor.isNativePlatform()) {
    try {
      const { Network } = await import("@capacitor/network");
      const status = await Network.getStatus();
      emit({ connected: status.connected, connectionType: (status.connectionType as any) ?? "unknown" });
      Network.addListener("networkStatusChange", (s) => {
        emit({ connected: s.connected, connectionType: (s.connectionType as any) ?? "unknown" });
      });
      return;
    } catch (err) {
      console.warn("[native-network] capacitor Network unavailable, falling back", err);
    }
  }
  if (typeof window !== "undefined") {
    const update = () => emit({
      connected: navigator.onLine,
      connectionType: navigator.onLine ? "unknown" : "none",
    });
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    update();
  }
}

export function getNetworkState(): NetworkState {
  if (!started) void start();
  return lastState;
}

export function subscribeNetwork(listener: Listener): () => void {
  if (!started) void start();
  listeners.add(listener);
  // emit current immediately for convenience
  try { listener(lastState); } catch { /* noop */ }
  return () => { listeners.delete(listener); };
}
